import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { importsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Upload, ArrowLeft, Loader2, FileSpreadsheet, Download, AlertCircle, Warehouse, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const CSV_TEMPLATE = `name,sku,quantity,unit_cost,billable_price,category
Example Air Filter 16x20,AF-1620,10,12.00,29.99,Filters
Example PVC Pipe 1/2 inch,PVC-050,25,3.50,8.99,Supplies
Example Copper Elbow 90deg,CU-90E,15,4.25,11.50,Plumbing`;

export const WarehouseImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'warehouse_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await importsAPI.upload('inventory_items', file);
      const sessionId = res.data.id;
      toast.success(`Uploaded ${res.data.total_rows} rows — reviewing results`);
      navigate(`/import-review/${sessionId}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to import CSV';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="warehouse-import-page">
        <Button variant="ghost" onClick={() => navigate('/warehouse')} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Warehouse
        </Button>

        <div className="max-w-2xl space-y-6">
          <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-sm" data-testid="additive-warning-box">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-base">This import ADDS items and quantities.</p>
                <p className="text-sm text-amber-800 mt-1">If an item with a matching SKU already exists, its quantity will be increased.</p>
                <p className="text-sm text-amber-800">New items will be created in the catalogue automatically.</p>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Warehouse className="w-6 h-6" />
                Import Stock to Warehouse
              </CardTitle>
              <CardDescription>
                Upload a CSV to add inventory items with quantities. Failed rows can be corrected and re-imported on the review page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Import Error</p>
                      <p className="text-sm text-destructive/80 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div
                data-testid="csv-upload-dropzone"
                className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => !loading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={loading}
                  data-testid="file-input"
                />
                {loading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                    <p className="text-lg font-semibold">Uploading & processing...</p>
                    <p className="text-sm text-muted-foreground mt-1">This may take a moment for large files</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-semibold text-foreground">Click to upload CSV file</p>
                    <p className="text-sm text-muted-foreground mt-2">You&apos;ll be taken to the review page to see results</p>
                  </div>
                )}
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-semibold mb-2">Expected CSV Columns:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li><span className="font-medium text-foreground">Required:</span> name (or Item Name)</li>
                  <li><span className="font-medium">Recommended:</span> sku, quantity (or qty)</li>
                  <li><span className="font-medium">Optional:</span> unit_cost, billable_price, category</li>
                </ul>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template CSV
                </Button>
              </div>

              <Button variant="outline" className="w-full" onClick={() => navigate('/import-history')} data-testid="view-import-history-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                View Import History
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseImport;
